import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { HISTORY } from '@ng-web-apis/common';
import { I18NextModule } from 'angular-i18next';
import { from, mergeMap, Subscription } from 'rxjs';
import { AvatarDirective } from 'src/app/common/avatar/avatar.directive';
import { IconComponent } from 'src/app/common/icon/icon.component';
import { NotLoggedService } from 'src/app/common/not-logged/not-logged.service';
import { OpenDialogService } from 'src/app/common/open-dialog/open-dialog.service';
import { RouteTranslateService } from 'src/app/common/route-translate/route-translate.service';
import { SlugService } from 'src/app/common/slug/slug.service';
import { UserNamePipe } from 'src/app/common/user-name/user-name.pipe';
import { UserService } from 'src/app/common/user/user.service';
import { getDefaultModalSettings } from 'src/app/common/utils/helpers';
import { Event, Post, Sharry, SharrySelection, Wonder } from 'src/types/graphql';
import { CommunityHomeService } from './community-home.service';
import { RouteTranslatePipe } from '../../../_shared/pipes/route-translate.pipe';
import { SlugPipe } from '../../../_shared/pipes/slug.pipe';
import { ContentTypeColorizerService } from '../../../_shared/services/content-type-colorizer.service';

@Component({
  selector: 'sharry-community-home',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    AsyncPipe,
    RouterLink,
    RouterLinkActive,
    RouterOutlet,
    RouteTranslatePipe,
    AvatarDirective,
    UserNamePipe,
    SlugPipe,
    I18NextModule,
    IconComponent,
    MatButtonModule,
  ],
  templateUrl: './community-home.component.html',
  styleUrls: ['./community-home.component.scss', './../community-common.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommunityHomeComponent implements OnDestroy {
  dialogSubscription: Subscription;
  createDisabled = false;

  constructor(
    public userService: UserService,
    private openDialogService: OpenDialogService,
    private router: Router,
    private routeTranslateService: RouteTranslateService,
    private communityHomeService: CommunityHomeService,
    private cd: ChangeDetectorRef,
    private notLoggedService: NotLoggedService,
    private contentTypeColorizerService: ContentTypeColorizerService,
    private slugService: SlugService,
    @Inject(HISTORY) private history: History,
  ) {
    const state = this.router?.lastSuccessfulNavigation?.extras?.state;
    if (!!state?.createPost && userService.user) {
      const obj = state.createPost;
      const sharryContentsIds = [obj.id];
      const { color } = this.contentTypeColorizerService.colorize(obj as any, 'short');
      const description = this.generateMentionDescription(color, obj);
      this.onCreateClick(false, description, sharryContentsIds);
      //this.history.replaceState({}, '');
    }

    if (!!state?.shareGroup && userService.user) {
      const group = state.shareGroup;
      const description = `::join-chat::/join-chat/${group.id}?name=${group.groupName}::join-chat::&nbsp;`;
      const title = `Unisciti al gruppo ${group.groupName}`.substring(0, 100);
      this.onCreateClick(false, description, null, title);
      //this.history.replaceState({}, '');
    }
  }

  onCreateClick(offer?: boolean, description?: string, sharryContentsIds?: string[], title?: string) {
    const fun = () => {
      this.createDisabled = true;
      this.dialogSubscription = from(import('../create-post-dialog/create-post-dialog.component'))
        .pipe(
          mergeMap(({ CreatePostDialogComponent }) =>
            this.openDialogService
              .openDialog<any, Partial<Post> & { mode: 'update' | 'create' }, Post | string>(
                CreatePostDialogComponent,
                {
                  ...getDefaultModalSettings(),
                  height: '84vh',
                  data: {
                    mode: 'create',
                    sharrySelection: offer ? SharrySelection.SeekPost : SharrySelection.SimplePost,
                    title,
                    description,
                    sharryContentsIds,
                  },
                },
              )
              .afterClosed(),
          ),
        )
        .subscribe({
          next: post => {
            this.createDisabled = false;
            this.cd.detectChanges();
            if (typeof post === 'string') {
              this.onCreateClick(post === 'seek');
            } else if (post) {
              this.communityHomeService.setRefresh(true);
              this.router.navigate([this.routeTranslateService.translateRoute('community/my-posts')]);
            }
          },
        });
    };
    this.notLoggedService.execute(() => fun());
  }

  onPostInfoClick() {
    this.dialogSubscription = from(
      import('src/app/pages/community/community-home/post-info-dialog/post-info-dialog.component'),
    ).subscribe({
      next: ({ PostInfoDialogComponent }) => {
        this.openDialogService.openDialog(PostInfoDialogComponent, {
          ...getDefaultModalSettings(),
        });
      },
    });
  }

  generateMentionDescription(color: string, obj: Wonder | Sharry | Event | Post) {
    return `<span class="mention" data-id="${
      obj.id
    }" data-value="<a class=&quot;internal-link&quot; href=&quot;${this.slugService.slug(
      obj,
    )}&quot; style=&quot;color: ${color}&quot; data-contentid=&quot;${
      obj.id
    }&quot;><img src=&quot;https://media.sharry.land/app/images/internal-link/internal-link-${
      obj.contentType
    }.webp&quot; alt=&quot;${obj.contentType}&quot;>${
      obj.title
    }</a><br>" data-denotation-char="">&#xFEFF;<span contenteditable="false"><span class="ql-mention-denotation-char"></span><a class="internal-link" href="${this.slugService.slug(
      obj,
    )}" style="color: ${color}" data-contentid="${
      obj.id
    }"><img src="https://media.sharry.land/app/images/internal-link/internal-link-${obj.contentType}.webp" alt="${
      obj.contentType
    }">${obj.title}</a><br></span>&#xFEFF;</span>`;
  }

  ngOnDestroy(): void {
    this.dialogSubscription?.unsubscribe();
  }
}
